import { type MouseEventHandler, type PropsWithChildren } from 'react'
import { useDebounce } from 'use-debounce'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'

import { cn } from '@/lib/utils'

export interface CellContentAssigneeProps {
  assignees: string[]
}

export const CellContentAssignee = ({ assignees }: CellContentAssigneeProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useDebounce(false, 250)

  const handleMouseEnter = () => {
    setIsPopoverOpen(true)
  }

  const handleMouseLeave = () => {
    setIsPopoverOpen(false)
  }

  const handleClickPopoverTrigger: MouseEventHandler = ($event) => {
    $event.stopPropagation()
    $event.preventDefault()
  }

  const handleClickPopoverContent: MouseEventHandler = ($event) => {
    $event.stopPropagation()
    $event.preventDefault()
  }

  const assigneeCount = assignees.length

  return (
    <div>
      {assigneeCount <= 2 && (
        <div className={cn('flex space-x-2')}>
          {assignees.map((name, index) => (
            <AssigneeBadge key={`assignee-${index}`}>{name}</AssigneeBadge>
          ))}
        </div>
      )}
      {assigneeCount > 2 && (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger
            className={cn('focus-within:outline-none')}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClickPopoverTrigger}
          >
            <AssigneeBadge interactive>{assigneeCount} Assignees</AssigneeBadge>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className={cn('w-fit', 'p-1')}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClickPopoverContent}
          >
            <ul>
              {assignees.map((name, index) => (
                <li key={`assignee-${index}`} className={cn('text-sm font-normal text-foreground', 'px-2 py-1.5')}>
                  {name}
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

const AssigneeBadge = ({ children, interactive = false }: PropsWithChildren<{ interactive?: boolean }>) => {
  return (
    <span
      className={cn(
        'flex items-center justify-center',
        'inline-block',
        'px-1 py-0.5',
        'bg-slate-50',
        'text-xs font-semibold text-slate-500',
        'whitespace-nowrap',
        'rounded-[5px] border border-slate-100',
        {
          'hover:border-slate-400 hover:bg-slate-500 hover:text-slate-50': interactive,
        }
      )}
    >
      {children}
    </span>
  )
}
